import { Close } from "@mui/icons-material";
import { Box, Divider, MenuItem, Select, styled, Typography, useTheme } from "@mui/material";
import { CircleButton } from "components/CircleButton";
import { StyledDrawer } from "components/SearchDrawer/styled";
import { ReactComponent as SendIcon } from "assets/icons/portfolio-send.svg";
import { Value } from "components/Value";
import { Chain, NATIVE_CURRENCY_CHECK_LIST } from "chainConfigs";
import { Card } from "components/Base/Containers";
import { BaseButton, TextButton } from "components/Base/Buttons";
import CoinAvatar, { NATIVE_TOKEN_MAP } from "components/CoinAvatar";
import { SmallNumberView } from "components/SmallValueView/SmallValueView";
import { StyledInputV2 } from "components/portfolio/styled";
import { TON } from "consts";
import { DarkDrawer } from "components/TradeDrawer";
import { minimumGasBufferUSD } from "tradeConfigs";

const StyledSelect = styled(Select)<{ noPointer?: boolean }>(({ theme, noPointer }) => ({
  pointerEvents: noPointer ? "none" : "auto",
  cursor: "pointer",
  borderRadius: `${theme.spacing(1.5)} 0 0 ${theme.spacing(1.5)}`,
  overflow: "hidden",

  "& :focus-visible": {
    backgroundColor: "transparent",
    border: "none",
    outline: "none",
  },

  ".MuiSelect-select": {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    padding: theme.spacing(1, 2),
    borderRadius: `${theme.spacing(1.5)} 0 0 ${theme.spacing(1.5)}`,
    backgroundColor: theme.palette.surface.silver,
    pointerEvents: noPointer ? "none" : "auto",
    width: "100%",
  },

  "& .MuiOutlinedInput-notchedOutline": {
    border: "none",
    borderRadius: `${theme.spacing(1.5)} 0 0 ${theme.spacing(1.5)}`,
    outline: "none",
  },

  "& .MuiSelect-icon": {
    color: theme.palette.text.secondary,
    fontSize: "24px",
  },

  "&:hover": {
    "& .MuiOutlinedInput-notchedOutline": {
      border: "none",
    },
  },
}));

const SendTokenDrawer = ({
  isOpen,
  isLoading,
  isSending,
  isOpenConfirmDrawer,
  close,
  indexedData,
  selectedTokenIndex,
  handleMaxAmount,
  handleOpenSearchDrawer,
  handleSelectTokenIndex,
  amount,
  amountRef,
  address,
  handleEnterAddress,
  handleEnterMemo,
  handleOpenConfirmDrawer,
  handleCloseConfirmDrawer,
  isContinueDisabled,
  isOpenSearchDrawer,
  handleCloseSearchDrawer,
  closeSearchDrawer,
  handleConfirm,
  handleOnInputChange,
  handleOnBlur,
}: {
  isOpen: boolean;
  isLoading: boolean;
  isSending: boolean;
  isOpenConfirmDrawer: boolean;
  close: () => void;
  indexedData: any[];
  selectedTokenIndex: number;
  handleMaxAmount: () => void;
  handleOpenSearchDrawer: () => void;
  handleSelectTokenIndex: (index: number) => void;
  amount: number;
  amountRef: React.RefObject<HTMLInputElement>;
  address: string;
  handleEnterAddress: (address: string) => void;
  handleEnterMemo: (memo: string) => void;
  handleOpenConfirmDrawer: () => void;
  handleCloseConfirmDrawer: () => void;
  isContinueDisabled: boolean;
  isOpenSearchDrawer: boolean;
  handleCloseSearchDrawer: () => void;
  closeSearchDrawer: (token: any) => void;
  handleConfirm: () => void;
  handleOnInputChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  handleOnBlur: () => void;
}) => {
  const theme = useTheme();

  return (
    <>
      <StyledDrawer
        open={isOpen && !isLoading}
        anchor="bottom"
        onClose={() => {}}
        sx={{ zIndex: 200 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            flexDirection: "column",
            width: "100%",
            height: "100%",
          }}>
          <Box sx={{ width: "100%", height: "100%", padding: "20px 20px 0" }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "end",
                alignItems: "center",
                width: "100%",
              }}>
              <CircleButton variant="white" onClick={close}>
                <Close fontSize="inherit" />
              </CircleButton>
            </Box>
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                flexDirection: "column",
                width: "100%",
                textAlign: "center",
                gap: "9px",
                marginY: "9px",
              }}>
              <SendIcon />
              <Typography variant="title-h1" color="text.primary">
                SEND TOKENS
              </Typography>
            </Box>
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                width: "100%",
                gap: "12px",
                flexDirection: "column",
              }}>
              <Card._3D cardType="dark">
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    width: "100%",
                    flexDirection: "column",
                    gap: "8px",
                    padding: "20px",
                  }}>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      width: "100%",
                    }}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: "4px",
                      }}>
                      <Typography variant="body-default" color="text.secondary">
                        Balance:{" "}
                      </Typography>
                      {NATIVE_CURRENCY_CHECK_LIST.includes(
                        indexedData[selectedTokenIndex]?.symbol,
                      ) ? (
                        <>
                          <Value
                            variant="body-default"
                            color="text.secondary"
                            value={indexedData[selectedTokenIndex].priceUsd}
                            smallNumberSignificantDigits={2}
                            isPrice
                          />{" "}
                          <Typography variant="body-default" color="text.secondary">
                            USD
                          </Typography>
                        </>
                      ) : (
                        <>
                          <Value
                            variant="body-default"
                            color="text.secondary"
                            value={indexedData[selectedTokenIndex]?.priceUsd}
                            smallNumberSignificantDigits={2}
                            isPrice
                          />{" "}
                          <Typography variant="body-default" color="text.secondary">
                            USD
                          </Typography>
                        </>
                      )}
                    </Box>
                    <Box sx={{ width: "fit-content" }}>
                      <BaseButton onClick={handleMaxAmount}>
                        <Box sx={{ padding: "4px 12px" }}>
                          <Typography variant="button-md">MAX</Typography>
                        </Box>
                      </BaseButton>
                    </Box>
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      width: "100%",
                      backgroundColor: theme.palette.surface.silver,
                      borderRadius: theme.spacing(1.5),
                    }}>
                    <Box
                      sx={{
                        display: "flex",
                      }}
                      onClick={handleOpenSearchDrawer}>
                      <StyledSelect
                        noPointer
                        value={selectedTokenIndex}
                        defaultValue={0}
                        sx={{ display: "flex", alignItems: "center", gap: theme.spacing(1) }}
                        MenuProps={{
                          slotProps: {
                            paper: {
                              sx: {
                                borderRadius: theme.spacing(1.5),
                                backgroundColor: theme.palette.surface.silver,
                              },
                            },
                          },
                        }}
                        onChange={(event) => handleSelectTokenIndex(event.target.value as number)}
                        IconComponent={undefined}>
                        {indexedData.map((token) => (
                          <MenuItem
                            key={token.index}
                            style={{ width: "100%" }}
                            selected={selectedTokenIndex === token.index}
                            value={token.index}>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                width: "100%",
                              }}>
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: theme.spacing(1.5),
                                }}>
                                <CoinAvatar
                                  style={{
                                    width: "28px",
                                    height: "28px",
                                  }}
                                  badgeStyle={{
                                    width: "14px",
                                    height: "14px",
                                  }}
                                  iconUri={
                                    token.imageUrl ? token.imageUrl : NATIVE_TOKEN_MAP[token.symbol]
                                  }
                                  chain={token.chain}
                                  size="sm"
                                />
                                <Typography
                                  variant="body-default"
                                  color={theme.palette.text.secondary}>
                                  {token.symbol}
                                </Typography>
                              </Box>
                              {selectedTokenIndex === token.index ? null : (
                                <Typography
                                  variant="body-default"
                                  color={theme.palette.text.secondary}>
                                  <SmallNumberView value={token.balance} isUSD />
                                </Typography>
                              )}
                            </Box>
                          </MenuItem>
                        ))}
                      </StyledSelect>
                      <Divider
                        orientation="vertical"
                        variant="middle"
                        flexItem
                        sx={{
                          border: `0.5px solid ${theme.palette.surface["light-bg"]}`,
                        }}
                      />
                    </Box>
                    <StyledInputV2
                      type="number"
                      ref={amountRef}
                      min={0}
                      max={indexedData[selectedTokenIndex]?.balance}
                      placeholder="0"
                      onChange={handleOnInputChange}
                      onBlur={handleOnBlur}
                    />
                    <Typography
                      variant="body-default"
                      sx={{
                        display: "inline",
                        marginRight: theme.spacing(1),
                      }}
                      color={theme.palette.text.secondary}>
                      {/* {indexedData[selectedTokenIndex]?.symbol} */}
                      USD
                    </Typography>
                  </Box>
                </Box>
              </Card._3D>
              <Card._3D cardType="dark">
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    width: "100%",
                    flexDirection: "column",
                    gap: "8px",
                    padding: "20px",
                  }}>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      width: "100%",
                    }}>
                    <Typography variant="body-default" color="text.secondary">
                      To
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      width: "100%",
                    }}>
                    <StyledInputV2
                      style={{ textAlign: "start" }}
                      placeholder="Enter Address"
                      onChange={(event) => handleEnterAddress(event.target.value)}
                    />
                  </Box>
                  {indexedData &&
                  indexedData[selectedTokenIndex] &&
                  indexedData[selectedTokenIndex].chain === TON ? (
                    <>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          width: "100%",
                        }}>
                        <Typography variant="body-default" color="text.secondary">
                          Memo (Optional)
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          width: "100%",
                        }}>
                        <StyledInputV2
                          style={{ textAlign: "start" }}
                          placeholder="Enter memo"
                          onChange={(event) => handleEnterMemo(event.target.value)}
                        />
                      </Box>
                    </>
                  ) : null}
                </Box>
              </Card._3D>
            </Box>
          </Box>
          <Box sx={{ margin: "12px 20px 20px", width: "100%", padding: "0 20px" }}>
            <BaseButton disabled={isContinueDisabled} onClick={handleOpenConfirmDrawer} fullWidth>
              <Box
                sx={{
                  padding: "12px",
                }}>
                <Typography
                  variant="title-h3"
                  color={isContinueDisabled ? "text.secondary" : "text.brand"}>
                  CONTINUE
                </Typography>
              </Box>
            </BaseButton>
          </Box>
        </Box>
      </StyledDrawer>

      {/* Search */}
      <DarkDrawer open={isOpenSearchDrawer} anchor="bottom" onClose={() => {}} sx={{ zIndex: 200 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            width: "100%",
            marginBottom: "16px",
          }}>
          <Box sx={{ width: "28px" }} />
          <Typography variant="title-h2">SELECT TOKEN</Typography>
          <CircleButton variant="white" onClick={handleCloseSearchDrawer}>
            <Close fontSize="inherit" />
          </CircleButton>
        </Box>
        <Box
          sx={{
            overflowY: "auto",
            maxHeight: "100%",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            gap: "8px",
          }}>
          {indexedData.map((token) => {
            const priceUsd = NATIVE_CURRENCY_CHECK_LIST.includes(token?.symbol)
              ? token?.priceUsd - (minimumGasBufferUSD[token.chain as Chain] ?? 0)
              : token?.priceUsd;

            if (priceUsd < 0) {
              return null;
            }

            return (
              <Box
                key={token.index}
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  width: "100%",
                  borderRadius: "16px",
                  border: `1px solid ${theme.palette.border.dark}`,
                  background: theme.palette.surface.silver,
                  padding: "12px",
                }}
                onClick={() => closeSearchDrawer(token)}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: "16px",
                  }}>
                  <CoinAvatar
                    style={{
                      width: "28px",
                      height: "28px",
                    }}
                    badgeStyle={{
                      width: "14px",
                      height: "14px",
                    }}
                    iconUri={token.imageUrl ? token.imageUrl : NATIVE_TOKEN_MAP[token.symbol]}
                    chain={token.chain}
                    size="sm"
                  />
                  <Typography variant="subtitle">{token.symbol}</Typography>
                </Box>
                <Value variant="body-default" value={priceUsd} isPrice />
              </Box>
            );
          })}
        </Box>
      </DarkDrawer>
      <DarkDrawer
        open={isOpenConfirmDrawer}
        anchor="bottom"
        onClose={() => {}}
        sx={{ zIndex: 200 }}>
        <Box>
          <Typography variant="title-h2">CONFIRM SENDING?</Typography>
        </Box>
        <Box
          sx={{
            display: "flex",
            justifyContent: "start",
            alignItems: "center",
            marginTop: "16px",
            marginBottom: "32px",
            gap: "8px",
          }}>
          <Typography variant="body-md" color={theme.palette.text.secondary}>
            Sending
          </Typography>
          <Value variant="body-md" value={amount} isPrice />
          <Typography variant="body-md" color={theme.palette.text.secondary}>
            {indexedData[selectedTokenIndex] ? indexedData[selectedTokenIndex].symbol : "N/A"} To{" "}
            {address}
          </Typography>
        </Box>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: "10px",
          }}>
          <TextButton
            variant="white"
            typo="title-h3"
            typoColor={isSending ? "text.secondary" : "text.btn-dark"}
            disabled={isSending}
            onClick={handleCloseConfirmDrawer}
            fullWidth
            innerSx={{
              paddingY: theme.spacing(1),
              width: "100%",
            }}>
            NO
          </TextButton>
          <TextButton
            typo="title-h3"
            variant={isSending ? "disabled" : "primary"}
            typoColor={isSending ? "text.secondary" : "text.brand"}
            onClick={handleConfirm}
            disabled={isSending}
            fullWidth
            innerSx={{
              paddingY: theme.spacing(1),
              width: "100%",
            }}>
            {isSending ? "SENDING..." : "YES"}
          </TextButton>
        </Box>
      </DarkDrawer>
    </>
  );
};

export { SendTokenDrawer };
